// © 2025 Camp Promethean, LLC. All Rights Reserved.
//
// Per-workflow field descriptors for the Hands-Free ReviewCard (T-205).
//
// One source of truth per workflow for: which proposed_data fields are editable,
// how to render each, which are required before commit, and how to convert form
// strings back into field_overrides values. Keys here MUST be a subset of the
// matching workflow's ALLOWED_OVERRIDE_KEYS in supabase/functions/hands-free-commit.

import type { ProposedData } from "./types";

export type FieldType =
  | "text"
  | "number"
  | "date"
  | "datetime"
  | "textarea"
  | "select"
  | "tags";

export interface FieldDesc {
  key: string;
  label: string;
  type: FieldType;
  /** Required before the Save button enables. */
  required?: boolean;
  /** Options for `select`. An empty value "" represents "unset". */
  options?: Array<{ value: string; label: string }>;
  /** Populate options from facility data (rooms, zones, crops). */
  entity?: "room" | "zone" | "crop";
  placeholder?: string;
  mono?: boolean;
  help?: string;
  /** Lay fields out in a multi-column row by giving them the same group id. */
  group?: string;
}

export interface WorkflowDescriptor {
  fields: FieldDesc[];
  /** proposed_data keys shown read-only in the provenance footer. */
  provenance: string[];
  /** Show the runoff raw-measurements block. */
  showMeasurements?: boolean;
}

const WET_METHOD_OPTIONS = [
  { value: "", label: "—" },
  { value: "batch", label: "Batch" },
  { value: "single_plant", label: "Single plant" },
  { value: "average", label: "Average" },
];

const MOOD_OPTIONS = [
  { value: "", label: "—" },
  { value: "great", label: "Great" },
  { value: "good", label: "Good" },
  { value: "neutral", label: "Neutral" },
  { value: "poor", label: "Poor" },
  { value: "bad", label: "Bad" },
];

export const WORKFLOW_DESCRIPTORS: Record<string, WorkflowDescriptor> = {
  runoff_log: {
    fields: [
      {
        key: "zone_id",
        label: "Location",
        type: "select",
        entity: "zone",
        required: true,
        help: "Pick the room and zone this reading belongs to.",
      },
      { key: "log_date", label: "Date", type: "date" },
      { key: "volume", label: "Volume (ml)", type: "number", group: "vpe", placeholder: "e.g. 250" },
      { key: "ph", label: "pH", type: "number", group: "vpe", placeholder: "e.g. 6.1" },
      { key: "ec", label: "EC (mS/cm)", type: "number", group: "vpe", placeholder: "e.g. 2.4" },
      { key: "notes", label: "Notes", type: "textarea", placeholder: "Anything else to remember…" },
    ],
    provenance: [],
    showMeasurements: true,
  },
  yield_batch: {
    fields: [
      { key: "batch_id", label: "Batch", type: "text", required: true, placeholder: "e.g. BD-2026-03" },
      { key: "harvest_date", label: "Harvest date", type: "date", required: true },
      { key: "room_id", label: "Room", type: "select", entity: "room", required: true },
      { key: "zone_id", label: "Location", type: "select", entity: "zone", required: true },
      { key: "strain_name", label: "Strain", type: "text", placeholder: "Strain name" },
      {
        key: "wet_weight_method",
        label: "Weigh method",
        type: "select",
        required: true,
        options: WET_METHOD_OPTIONS,
      },
      { key: "wet_weight", label: "Wet weight (g)", type: "number", group: "wt", placeholder: "grams" },
      { key: "plant_count", label: "Plant count", type: "number", group: "wt", placeholder: "plants" },
      { key: "dry_weight_a", label: "Dry weight (g)", type: "number", group: "wt", placeholder: "grams" },
      { key: "notes", label: "Notes", type: "textarea", placeholder: "Anything else to remember…" },
    ],
    provenance: [],
  },
  diary_entry: {
    fields: [
      { key: "crop_id", label: "Crop", type: "select", entity: "crop", required: true },
      { key: "entry_date", label: "Date", type: "date" },
      { key: "mood", label: "Mood", type: "select", options: MOOD_OPTIONS, group: "mt" },
      { key: "tags", label: "Tags", type: "tags", group: "mt", placeholder: "pests, defoliation" },
      { key: "notes", label: "Note", type: "textarea", placeholder: "What did you observe?" },
    ],
    provenance: [],
  },
  crop_note: {
    fields: [
      { key: "crop_id", label: "Crop", type: "select", entity: "crop", required: true },
      { key: "note", label: "Note", type: "textarea", required: true, placeholder: "What did you observe?" },
      { key: "event_date", label: "Date", type: "date" },
      { key: "mood", label: "Mood", type: "select", options: MOOD_OPTIONS, group: "mt" },
      { key: "tags", label: "Tags", type: "tags", group: "mt", placeholder: "ipm, canopy" },
    ],
    provenance: [],
  },
  application_log: {
    fields: [
      { key: "room_id", label: "Room", type: "select", entity: "room", required: true },
      { key: "zone_id", label: "Location", type: "select", entity: "zone", required: true },
      { key: "applicator", label: "Applicator", type: "text", required: true, placeholder: "Who applied" },
      { key: "application_date", label: "Date & time", type: "datetime", required: true },
      { key: "mixed_volume", label: "Mixed volume", type: "number", group: "vol", required: true },
      { key: "applied_volume", label: "Applied volume", type: "number", group: "vol", required: true },
      {
        key: "volume_unit",
        label: "Unit",
        type: "select",
        required: true,
        options: [
          { value: "", label: "—" },
          { value: "gal", label: "Gallons" },
          { value: "L", label: "Liters" },
          { value: "mL", label: "Milliliters" },
          { value: "oz", label: "Fluid oz" },
        ],
        group: "vol",
      },
      { key: "target_pest", label: "Target pest", type: "text", placeholder: "e.g. spider mites" },
      { key: "purpose", label: "Purpose", type: "text", placeholder: "Preventative, curative…" },
      { key: "notes", label: "Notes", type: "textarea", placeholder: "Anything else to remember…" },
    ],
    provenance: [],
  },
};

export function getDescriptor(workflowType: string): WorkflowDescriptor {
  return WORKFLOW_DESCRIPTORS[workflowType] ?? WORKFLOW_DESCRIPTORS.runoff_log;
}

// ── form value <-> proposed_data conversion ──────────────────────────────────

export type FormValues = Record<string, string>;

function proposedToFormString(type: FieldType, raw: unknown): string {
  if (raw == null) {
    if (type === "date") return new Date().toISOString().split("T")[0];
    return "";
  }
  switch (type) {
    case "date":
    case "datetime":
      return String(raw).split("T")[0];
    case "number":
      return typeof raw === "number" ? String(raw) : String(raw);
    case "tags":
      return Array.isArray(raw) ? (raw as string[]).join(", ") : String(raw);
    default:
      return String(raw);
  }
}

export function proposedDataToFormValues(
  descriptor: WorkflowDescriptor,
  pd: ProposedData,
): FormValues {
  const out: FormValues = {};
  for (const f of descriptor.fields) {
    out[f.key] = proposedToFormString(f.type, pd[f.key]);
  }
  return out;
}

function parseOptionalFloat(s: string): number | null {
  const t = s.trim();
  if (t === "") return null;
  const n = parseFloat(t);
  return isNaN(n) ? null : n;
}

/** Convert one form string into the value type expected by the override payload. */
function formToValue(type: FieldType, s: string): unknown {
  switch (type) {
    case "number":
      return parseOptionalFloat(s);
    case "tags":
      return s.split(",").map((t) => t.trim()).filter(Boolean);
    case "select":
    case "text":
    case "date":
    case "datetime":
    case "textarea":
      return s.trim() === "" ? null : s.trim();
    default:
      return s;
  }
}

/** Normalize a proposed_data value for change-comparison against a form value. */
function normalizeProposed(type: FieldType, raw: unknown): unknown {
  if (type === "number") return typeof raw === "number" ? raw : raw == null ? null : raw;
  if (type === "tags") return Array.isArray(raw) ? (raw as string[]).join("|") : "";
  if (type === "date" || type === "datetime") {
    return raw == null ? null : String(raw).split("T")[0];
  }
  return raw == null ? null : String(raw);
}

function normalizeForm(type: FieldType, s: string): unknown {
  if (type === "tags") {
    return s.split(",").map((t) => t.trim()).filter(Boolean).join("|");
  }
  const v = formToValue(type, s);
  return v;
}

/** Build field_overrides: only keys whose value changed from proposed_data. */
export function buildFieldOverrides(
  descriptor: WorkflowDescriptor,
  proposed: ProposedData,
  form: FormValues,
): Record<string, unknown> {
  const overrides: Record<string, unknown> = {};
  for (const f of descriptor.fields) {
    const before = normalizeProposed(f.type, proposed[f.key]);
    const after = normalizeForm(f.type, form[f.key] ?? "");
    if (before !== after) {
      overrides[f.key] = formToValue(f.type, form[f.key] ?? "");
    }
  }
  return overrides;
}

export function isDirty(overrides: Record<string, unknown>): boolean {
  return Object.keys(overrides).length > 0;
}

/** True when every required field has a non-empty form value. */
export function requiredFieldsFilled(
  descriptor: WorkflowDescriptor,
  form: FormValues,
): boolean {
  return descriptor.fields
    .filter((f) => f.required)
    .every((f) => (form[f.key] ?? "").trim() !== "");
}

export { parseOptionalFloat };
